import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import AppShell from "./components/layout/AppShell";
import "./index.css"; // Default Vite CSS, can be kept or removed if global.css covers all needs
import "./styles/global.css"; // Global tokens
import "./styles/isolation.css"; // Scoped isolation under widget/fullpage roots
import { mountInShadowRoot } from "./utils/shadowMount";
import * as Sentry from "@sentry/react"; // Added Sentry import
import { logError } from "./utils/logging"; // Import logging utility

// Initialize Sentry
// IMPORTANT: Replace with your actual DSN and configure release/environment properly.
if (import.meta.env.PROD) {
  // Only initialize Sentry in production builds
  Sentry.init({
    dsn: "https://4f7df707eb7bac1c427d866070f43e6c@o4509372859285504.ingest.de.sentry.io/4509372860792912",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Additional Replay configuration, if needed:
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions for performance monitoring. Adjust as needed.
    // Session Replay
    replaysSessionSampleRate: 0.05, // This sets the sample rate at 5%. Adjust as needed.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, sample the session when an error occurs.

    environment: import.meta.env.MODE, // e.g., 'production', 'development'
    release: "smartie-frontend@" + __APP_VERSION__, // Using __APP_VERSION__ defined via Vite
    // For release, ensure your Vite config defines __APP_VERSION__ (e.g., from package.json version)
    // Example in vite.config.ts: define: { '__APP_VERSION__': JSON.stringify(process.env.npm_package_version) }
  });
}

// Dev helper: allow forcing mount mode via URL, e.g. ?mode=fullpage or ?mode=widget
const params = new URLSearchParams(window.location.search);
const forcedMode = params.get("mode"); // "fullpage" | "widget" | null

let widgetRoot = document.getElementById("smartie-chat-widget-root");
let fullpageRoot = document.getElementById("smartie-fullpage-root");

// If a mode is forced but the corresponding root is missing (common in Vite dev), create it
if (!fullpageRoot && forcedMode === "fullpage") {
  fullpageRoot = document.createElement("div");
  fullpageRoot.id = "smartie-fullpage-root";
  document.body.appendChild(fullpageRoot);
}

if (!widgetRoot && forcedMode === "widget") {
  widgetRoot = document.createElement("div");
  widgetRoot.id = "smartie-chat-widget-root";
  document.body.appendChild(widgetRoot);
}

if (fullpageRoot) {
  // In development, mount directly to avoid Shadow DOM stylesheet isolation issues (Vite injects <style> tags)
  if (import.meta.env.DEV) {
    ReactDOM.createRoot(fullpageRoot).render(
      <React.StrictMode>
        <AppShell />
      </React.StrictMode>,
    );
  } else {
    // In production (WordPress), mount inside Shadow DOM for style isolation and clone plugin styles
    mountInShadowRoot(fullpageRoot, (container) => {
      ReactDOM.createRoot(container).render(
        <React.StrictMode>
          <AppShell />
        </React.StrictMode>,
      );
    });
  }
} else if (widgetRoot) {
  ReactDOM.createRoot(widgetRoot).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
} else {
  logError(
    "Failed to find Smartie root elements. Expected #smartie-fullpage-root or #smartie-chat-widget-root.",
  );
}
